import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TreeNode } from 'primeng/api';

// PrimeNG Modules
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TooltipModule } from 'primeng/tooltip';
import { UiOrchestratorService } from '../../core/services/ui-orchestrator.service';

// Shared Components
import { PageLayoutComponent } from '../../shared/ui/page-layout/page-layout.component';
import { DynamicTableComponent } from '../../shared/components/dynamic-table/dynamic-table.component';
import { TableColumn, RowAction, ActionEvent, TableExportConfig } from '../../shared/components/dynamic-table/dynamic-table.interface';
import { toPersonView } from '../../shared/models/person-view.model';
import { PageTitleService } from '../../services/page-title.service';
import { EmployeeStateService } from './employee-state.service';
import { MetadataService } from '../../core/services/metadata.service';
import { RegistrationConfigService } from '../../core/services/registration-config.service';
import { Employee } from '../../core/models/employee.model';
import { getTagSeverity } from '../../shared/constants/ui.constants';
import { PermissionResolverService } from '../../core/services/permission-resolver.service';
import { SCOPES } from '../../core/constants/permissions';
import { SelectButtonModule } from 'primeng/selectbutton';
import { PageConfig, FilterConfig, ImportConfig } from '../../shared/models/page-config.model';

// Dialog Components (Imported locally from the real employees directory)
import { EmployeeDetailsDialogComponent } from './components/employee-details-dialog/employee-details-dialog.component';
import { CsvImportDialogComponent, ParsedImportData } from '../../shared/ui/csv-import-dialog/csv-import-dialog.component';

@Component({
  selector: 'app-employee-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    PageLayoutComponent,
    DynamicTableComponent,
    SelectButtonModule,
    TooltipModule,
    CsvImportDialogComponent
  ],
  providers: [EmployeeStateService],
  templateUrl: './employee-management.component.html'
})
export class EmployeeManagementComponent implements OnInit {
  protected employeeState = inject(EmployeeStateService);
  private pageTitleService = inject(PageTitleService);
  private uiOrchestrator = inject(UiOrchestratorService);
  private router = inject(Router);
  protected metadata = inject(MetadataService);
  private resolver = inject(PermissionResolverService);
  private registrationConfigService = inject(RegistrationConfigService);

  perms = this.resolver.resolveMap({
    canCreate: SCOPES.EMPLOYEE_CREATE,
    canEdit:   SCOPES.EMPLOYEE_EDIT,
    canDelete: SCOPES.EMPLOYEE_DELETE,
    canImport: SCOPES.EMPLOYEE_IMPORT,
  });

  // --- Page Header (Dynamic Config) ---
  pageConfig: PageConfig = {
    title: 'Our People',
    subtitle: 'Everyone in your organization — all in one place.',
    hasFilters: false,
    headerActions: [
      {
        id: 'import',
        label: 'Import',
        icon: 'pi pi-file-import',
        tooltip: 'Bulk import people',
        outlined: true,
        severity: 'primary',
        visible: () => this.perms().canImport
      },
      {
        id: 'add',
        label: 'Add Person',
        icon: 'pi pi-plus',
        tooltip: 'Add a new person',
        severity: 'primary',
        visible: () => this.perms().canCreate
      }
    ]
  };

  get filterConfigs(): FilterConfig[] {
    return [
      {
        id: 'search',
        type: 'search',
        placeholder: 'Search people...',
        value: () => this.employeeState.searchQuery(),
        onChange: (val) => this.employeeState.setSearchQuery(val ?? ''),
        defaultValue: ''
      },
      ...this.employeeState.filterFields().map(field =>
        field.type === 'treeselect'
          ? {
              id: field.fieldId,
              type: 'treeselect' as const,
              placeholder: field.label,
              value: () => this.employeeState.orgNodeFilterNodes(),
              onChange: (nodes: TreeNode[] | null) => this.employeeState.setOrgNodeFilter(nodes),
              defaultValue: []
            }
          : {
              id: field.fieldId,
              type: 'select' as const,
              placeholder: field.label,
              options: () => field.options,
              value: () => this.employeeState.getFilter(field.fieldId),
              onChange: (val: string | null) => this.employeeState.setFilter(field.fieldId, val),
              defaultValue: null
            }
      )
    ];
  }

  // --- UI State (Ephemeral) ---
  isImportDialogOpen = signal(false);
  selectedEmployees = signal<Employee[]>([]);

  // --- Import Configuration ---
  get importConfig(): ImportConfig {
    return {
      entityName: 'Person',
      templateFilename: 'people_import_template',
      maxFileSizeMb: 5,
      formKey: this.registrationConfigService.formKey,
      validateEndpoint: '/employees/bulk/validate',
      uploadEndpoint: '/employees/bulk',
      fields: [
        { field: 'name', label: 'Full Name', required: true, sampleValue: 'John Doe', hint: 'The full legal name.' },
        { field: 'email', label: 'Email Address', required: true, sampleValue: 'john.doe@example.com', hint: 'Work email address.' },
        { field: 'role', label: 'Role', required: true, sampleValue: 'USER', hint: 'ADMIN, MANAGER, or USER' },
        { field: 'employmentType', label: 'Type', required: true, sampleValue: 'Full-time', hint: 'Full-time, Part-time, Contract, etc.' },
        { field: 'joinDate', label: 'Join Date', required: true, sampleValue: '2024-01-15', hint: 'YYYY-MM-DD format' },
        { field: 'status', label: 'Status', required: true, sampleValue: 'Active', hint: 'Active, Onboarding, or Terminated' }
      ],
      notes: [
        'Joined date must be in YYYY-MM-DD format.',
        'Role must be one of: ADMIN, MANAGER, USER.',
        'Existing emails will result in an update instead of a new record.'
      ]
    };
  }

  emptyTableMessage = computed(() => {
    const hasFilters = this.employeeState.searchQuery() ||
      Object.values(this.employeeState.activeFilters()).some(v => v !== null && v !== undefined) ||
      this.employeeState.orgNodeFilterNodes().length > 0;

    return hasFilters
      ? 'No people match your search.'
      : 'No one here yet. Start by adding your first team member.';
  });

  // --- Table Configuration (driven by API via EmployeeStateService) ---
  get tableColumns() { return this.employeeState.tableColumns(); }

  tableActions: RowAction[] = [
    {
      id: 'view',
      icon: 'pi pi-eye',
      severity: 'secondary',
      tooltip: 'View Profile',
      position: 'inline'
    },
    {
      id: 'edit',
      icon: 'pi pi-pencil',
      severity: 'info',
      tooltip: 'Edit',
      position: 'menu',
      disabled: () => !this.perms().canEdit
    },
    {
      id: 'delete',
      icon: 'pi pi-trash',
      severity: 'danger',
      tooltip: 'Delete',
      position: 'menu',
      visible: () => this.perms().canDelete
    }
  ];

  exportConfig: TableExportConfig = {
    filename: 'people_report',
    title: 'People Directory Report',
    showPrint: true,
    showCopy: true,
    showExcel: true,
    showCsv: true,
    showPdf: true
  };

  constructor() {
    this.pageTitleService.setTitle(this.pageConfig.title);
  }

  getDisplayName(employee: any): string {
    return toPersonView(employee).name;
  }

  ngOnInit() {
    this.employeeState.loadEmployees();
    this.employeeState.loadDraftEmployees();
    this.employeeState.loadRegistrationConfig();
  }



  viewEmployee(employee: Employee) {
    this.uiOrchestrator.openDetailDialog(
      EmployeeDetailsDialogComponent,
      { employee },
      'full-content-dialog'
    );
  }

  confirmDelete(employee: any) {
    const displayName = this.getDisplayName(employee);
    this.uiOrchestrator.confirmDelete(displayName, () => {
      this.employeeState.deleteEmployee(employee.id).subscribe({
        next: () => {
          this.uiOrchestrator.showSuccess(displayName, 'deleted');
        },
        error: () => {
          this.uiOrchestrator.showError(`Could not remove ${displayName}. Please try again.`);
        }
      });
    });
  }

  deleteSelectedEmployees() {
    const selected = this.selectedEmployees();
    if (selected.length === 0) return;

    this.uiOrchestrator.confirm({
      header: 'Remove Selected',
      icon: 'pi pi-exclamation-circle text-red-500',
      message: `Are you sure you want to remove ${selected.length} selected people? This action cannot be undone.`,
      acceptLabel: 'Remove Selected',
      rejectLabel: 'Cancel',
      acceptIcon: 'none',
      rejectIcon: 'none',
      accept: () => {
        const ids = selected.map(emp => emp.id);
        this.employeeState.bulkDeleteEmployees(ids).subscribe({
          next: () => {
            this.selectedEmployees.set([]);
            this.uiOrchestrator.toast({
              severity: 'success',
              summary: 'Bulk Deleted',
              detail: `${selected.length} profile${selected.length !== 1 ? 's' : ''} have been removed.`
            });
          },
          error: () => {
            this.uiOrchestrator.showError('Could not delete the selected people. Please try again.');
          }
        });
      }
    });
  }

  onHeaderAction(actionId: string) {
    switch (actionId) {
      case 'add':
        this.router.navigate(['/employees/register']);
        break;
      case 'import':
        this.isImportDialogOpen.set(true);
        break;
    }
  }

  onImportReady(data: ParsedImportData) {
    this.employeeState.loadEmployees();
  }

  onTableAction(event: ActionEvent) {
    const { action, data } = event;

    switch (action.id) {
      case 'view':
        this.viewEmployee(data);
        break;
      case 'edit':
        this.router.navigate(['/employees/edit', data.id]);
        break;
      case 'delete':
        this.confirmDelete(data);
        break;
    }
  }
}
